import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import inspectionsService from "../services/inspectionsService";
import styles from "../assets/inspections.module.css";

const EmailReminderCreate = () => {
  const { buildingManagerId } = useParams();
  const navigate = useNavigate();

  const [buildingManager, setBuildingManager] = useState(null);
  const [properties, setProperties] = useState([]);
  const [selectedProperties, setSelectedProperties] = useState({});
  const [formData, setFormData] = useState({
    subject: "",
    content: "",
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [files, setFiles] = useState([null]); // Array of file objects
  const [filesCounter, setFilesCounter] = useState(1);

  useEffect(() => {
    loadData();
  }, [buildingManagerId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await inspectionsService.getEmailReminderData(buildingManagerId);
      
      if (data.buildingManager) {
        setBuildingManager(data.buildingManager);
      }
      
      if (data.properties) {
        setProperties(data.properties);
        // Initialize all properties as selected by default
        const initialSelection = {};
        data.properties.forEach((prop) => {
          initialSelection[prop.id] = true;
        });
        setSelectedProperties(initialSelection);
      }
    } catch (error) {
      console.error("Error loading email reminder data:", error);
      toast.error("Failed to load email reminder data");
      
      // Set default data for testing
      setBuildingManager({
        id: buildingManagerId,
        fullName: `Building Manager ${buildingManagerId}`,
        email: `manager${buildingManagerId}@example.com`,
      });
      setProperties([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePropertyToggle = (propertyId) => {
    setSelectedProperties((prev) => ({
      ...prev,
      [propertyId]: !prev[propertyId],
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (index, e) => {
    const newFiles = [...files];
    newFiles[index] = e.target.files[0];
    setFiles(newFiles);
  };

  const addFileField = () => {
    setFiles([...files, null]);
    setFilesCounter(filesCounter + 1);
  };

  const removeFileField = (index) => {
    const newFiles = files.filter((_, i) => i !== index);
    setFiles(newFiles);
    setFilesCounter(filesCounter - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.subject.trim()) {
      toast.error("Subject is required");
      return;
    }

    if (!formData.content.trim()) {
      toast.error("Content is required");
      return;
    }

    const selectedPropertyIds = Object.keys(selectedProperties).filter(
      (id) => selectedProperties[id]
    );

    if (selectedPropertyIds.length === 0) {
      toast.error("Please select at least one property");
      return;
    }

    try {
      setSubmitting(true);
      
      // Create FormData for file uploads
      const formDataToSend = new FormData();
      formDataToSend.append('subject', formData.subject);
      formDataToSend.append('content', formData.content);
      formDataToSend.append('filesCounter', filesCounter);
      selectedPropertyIds.forEach((id) => {
        formDataToSend.append('properties[]', id);
      });
      
      // Append files
      files.forEach((file, index) => {
        if (file) {
          formDataToSend.append('files[]', file);
        }
      });

      await inspectionsService.sendEmailReminder(buildingManagerId, formDataToSend);

      toast.success("Email reminder sent successfully!");
      navigate(`/inspections/building-manager/${buildingManagerId}`);
    } catch (error) {
      console.error("Error sending email reminder:", error);
      toast.error("Failed to send email reminder");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className="panel">
          <div className="panel-body">
            <p>Loading email reminder form...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      {/* Breadcrumb */}
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <a href="/dashboard">Dashboard</a>
          </li>
          <li className="breadcrumb-item">
            <a href="/inspections">Inspections</a>
          </li>
          <li className="breadcrumb-item">
            <a href={`/inspections/building-manager/${buildingManagerId}`}>
              {buildingManager?.fullName || "Building Manager"} Calendar
            </a>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            Send Email Reminder
          </li>
        </ol>
      </nav>

      <div className="row">
        <div className="col-md-12">
          <div className="panel panel-default panel-form">
            <div className="panel-heading panel-title">
              <h3>
                <span className="text-muted">Send email reminder to </span>
                {buildingManager?.fullName || "Building Manager"}
              </h3>
            </div>
            <div className="panel-body">
              <form onSubmit={handleSubmit} id="sendEmailForm">
                <fieldset className="form-horizontal">
                  {/* Properties Section */}
                  <div className="panel-title title-standard">
                    <h3>Properties</h3>
                  </div>

                  <div className="panel-group border-down margin-down">
                    {properties.length > 0 ? (
                      <table className="table table-striped table-hover">
                        <thead>
                          <tr>
                            <th>Send email?</th>
                            <th>ID</th>
                            <th>Name</th>
                            <th>Route</th>
                            <th>Inspection Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {properties.map((property) => (
                            <tr key={property.id}>
                              <td>
                                <input
                                  type="checkbox"
                                  checked={selectedProperties[property.id] || false}
                                  onChange={() => handlePropertyToggle(property.id)}
                                  id={`properties[${property.id}]`}
                                />
                              </td>
                              <td>{property.property_id || property.id}</td>
                              <td>{property.name || "-"}</td>
                              <td>
                                {property.inspectionRoute?.name ||
                                  property.route?.name ||
                                  "-"}
                              </td>
                              <td>
                                {property.monthly_inspection_status_text ||
                                  property.monthlyInspectionStatus ||
                                  "-"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p>No properties available for this building manager.</p>
                    )}
                  </div>

                  {/* Email Section */}
                  <div className="panel-title title-standard">
                    <h3>Email</h3>
                  </div>

                  <div className="panel-group border-down margin-down">
                    <div className="form-group">
                      <label className="control-label col-sm-2 label-required">
                        Subject
                      </label>
                      <div className="col-sm-10">
                        <input
                          type="text"
                          name="subject"
                          value={formData.subject}
                          onChange={handleInputChange}
                          className="form-control"
                          required
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="control-label col-sm-2 label-required">
                        Content
                      </label>
                      <div className="col-sm-10">
                        <textarea
                          name="content"
                          value={formData.content}
                          onChange={handleInputChange}
                          className="form-control"
                          rows="10"
                          required
                        />
                      </div>
                    </div>

                    {/* Attachments Section */}
                    <div className="form-group">
                      <label className="control-label col-sm-2">Attachments</label>
                      <div className="col-sm-5">
                        <button
                          type="button"
                          className="btn btn-default add_file_field_button"
                          onClick={addFileField}
                          style={{ marginBottom: '10px' }}
                        >
                          Add file
                        </button>
                        <input type="hidden" id="filesCounter" name="filesCounter" value={filesCounter} />
                        <div className="col-sm-12">
                          <div className="form-group">
                            <div className="input_file_fields_wrap">
                              {files.map((file, index) => (
                                <div key={index} style={{ marginBottom: '3px' }}>
                                  <input
                                    type="file"
                                    name="files[]"
                                    className="form-control"
                                    onChange={(e) => handleFileChange(index, e)}
                                  />
                                  {index > 0 && (
                                    <a
                                      href="#"
                                      className="remove_field"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        removeFileField(index);
                                      }}
                                      style={{ marginLeft: '10px', color: '#d9534f' }}
                                    >
                                      Remove
                                    </a>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <fieldset className="form-horizontal">
                    <div className="form-group text-right">
                      <div className="col-sm-12">
                        <button
                          type="submit"
                          className="btn btn-head btn-standard"
                          disabled={submitting}
                        >
                          {submitting ? "Sending..." : "Send email"}
                        </button>
                      </div>
                    </div>
                  </fieldset>
                </fieldset>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailReminderCreate;

